import {
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowUp,
  IconBrandWhatsapp,
  IconMapPin,
  IconPhone,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  StateBadge,
  StickyActionBar,
  useColumns,
} from "../../components/ui";
import { deliveryState } from "../../components/ui/statusBadge";
import {
  api,
  type DeliveryAgent,
  type DeliveryScope,
  type DeliveryTask,
} from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";
import { STATUS_LABELS, carryingLabel, partlyCollected } from "./deliveryMeta";

/**
 * One screen for three lists, chosen by the path:
 *
 *   /service/assign-deliveries  raised but given to nobody - to hand out
 *   /delivery/round             what is assigned to whoever is looking
 *   /delivery/all               every delivery still to happen
 *
 * The first of those is a SERVICE route, which looks odd in a Delivery screen
 * and is deliberate: the business assigns deliveries through the Service
 * Manager, so the queue sits with their work. The screen did not need to move
 * with it - the scope falls out of the path, and anything that is not /round
 * or /all is the unassigned pile.
 *
 * A delivery AGENT gets their own round whichever of these they open. The
 * server enforces that - "every delivery agent will only see the order
 * assigned to him" - so the headings adapt rather than promising a view the
 * API will not return.
 *
 * A FAILED task stays in the list: the goods still have to get there, so a
 * second attempt has to start somewhere.
 *
 * WHAT THE ROUND VIEW ADDS
 *
 * The round comes back in the order the Store Manager set (roundSequence,
 * unsequenced last), so "the next stop" is a real answer rather than a guess
 * the client makes. It is drawn as its own card, because an agent standing
 * outside one customer's shop needs one address, one phone number and one
 * amount - not a list to scan.
 *
 * WHAT THE TODAY STRIP DOES NOT SAY
 *
 * The brief's strip reads "1 of 4 done". This list returns only OPEN stops -
 * the server filters to ASSIGNED and FAILED - so the number delivered today
 * is not in it, and a "done" count would either be invented or need a second
 * request for history nobody asked to see. The strip states what is true of
 * the round in hand: how many stops, how many units, what there is to
 * collect, and how many attempts have already failed.
 */

function customerOf(task: DeliveryTask) {
  return task.order?.customer ?? task.serviceJob?.customer ?? null;
}

/** Units on the van for this stop. A service return is always the one battery. */
function unitsOf(task: DeliveryTask): number {
  if (task.order) {
    return task.order.lineItems.reduce(
      (sum, line) => sum + line.quantityFulfilled,
      0,
    );
  }
  return 1;
}

function phoneOf(task: DeliveryTask): string | null {
  const phone = customerOf(task)?.phone ?? null;
  return phone && phone.trim() !== "" ? phone : null;
}

export function DeliveryQueue() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const scope: DeliveryScope = pathname.endsWith("/round")
    ? "mine"
    : pathname.endsWith("/all")
      ? "open"
      : "unassigned";

  const [tasks, setTasks] = useState<DeliveryTask[]>();
  const [error, setError] = useState<string>();
  const [agents, setAgents] = useState<DeliveryAgent[]>([]);

  /** The manager's selection, for handing several out at once. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [assignTo, setAssignTo] = useState<string>("");
  const [assigning, setAssigning] = useState(false);

  const { can, hasRole, user } = useAuth();
  /**
   * TWO CAPABILITIES, NOT ONE, and the split matches the server's.
   *
   * `canAssign` is who may hand work out - the Store Manager, an Admin, and
   * since the business moved service delivery assignment, the Service
   * Manager. `isOverseer` is who may do the rest of a manager's job on this
   * screen: order a round, and see the reconcile panel.
   *
   * A Service Manager holds SERVICE only. They deliberately do NOT hold the
   * DELIVERY module, so they reach this screen through its Service route and
   * nothing else in Delivery - see DELIVERY_ASSIGNERS in delivery.service for
   * the same split on the server, which is the one that actually enforces it.
   */
  const canAssign = hasRole("SERVICE_MANAGER", "STORE_MANAGER", "ADMIN");
  const isOverseer = hasRole("STORE_MANAGER", "ADMIN");
  /**
   * Whether a row opens.
   *
   * The task detail is a Delivery screen - the handover form, the proof
   * capture, the failure reasons - so it needs the module, not merely the
   * right to assign. A Service Manager assigning a round should not be handed
   * the means to complete one, and a row that navigated them somewhere they
   * would be bounced from is worse than a row that does not move.
   */
  const canOpenTask = can("DELIVERY");

  async function load() {
    try {
      setError(undefined);
      setTasks(undefined);
      const { tasks: loaded } = await api.deliveryTasks(scope);
      setTasks(loaded);
      setPicked(new Set());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the queue.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Only somebody who can hand work out needs the list of people to hand it to.
  useEffect(() => {
    if (!canAssign) return;
    api
      .deliveryAgents()
      .then(({ agents: loaded }) => setAgents(loaded))
      .catch(() => setAgents([]));
  }, [canAssign]);

  const heading = !canAssign
    ? "Delivery List"
    : scope === "mine"
      ? "Delivery List"
      : scope === "open"
        ? "Delivery Summary"
        : "Delivery Executive Allotment";

  /** The round in hand, for the strip. Counted over what is on screen. */
  const today = useMemo(() => {
    const rows = tasks ?? [];
    return {
      stops: rows.length,
      units: rows.reduce((sum, task) => sum + unitsOf(task), 0),
      // Recomputed from outstandingAmount, which the SERVER derives from the
      // bill's payments - never from codAmountDue, which goes stale the
      // moment Marketing collects in the field.
      toCollect: rows.reduce((sum, task) => sum + Number(task.outstandingAmount), 0),
      failed: rows.filter((task) => task.status === "FAILED").length,
    };
  }, [tasks]);

  const showRound = scope === "mine" && (tasks?.length ?? 0) > 0;
  const nextStop = showRound ? tasks![0] : undefined;
  /**
   * The round's remaining stops, after the Next stop card.
   *
   * ONLY ever the round. It used to fall back to `tasks ?? []`, which on any
   * MANAGER scope - unassigned, open, all - was the entire list, and the
   * cards below are drawn at every width. So /delivery/queue and
   * /delivery/all rendered the table and then a card for every row of it
   * underneath: the same seven deliveries, twice, on one screen. Reported
   * from a screenshot.
   *
   * It was correct before those queues moved into ListView, which draws the
   * table on desktop and the cards on a phone itself. This fallback was what
   * used to do that job, and it should have gone at the same time.
   *
   * An agent's own round keeps its cards at EVERY width, deliberately - a
   * round is walked one stop at a time and the Next stop card is the point of
   * the screen - which is why this is narrowed rather than removed.
   */
  const rest = showRound ? tasks!.slice(1) : [];

  /**
   * Who may hand work out on THIS list, which is not the same question on
   * both of them.
   *
   *   unassigned  the first assignment - whoever may assign. The Service
   *               Manager reaches this list and this is their job on it.
   *   open        REASSIGNMENT - overseers only. Delivery Summary is a
   *               DELIVERY screen; a Service Manager cannot reach it at all
   *               (no module), and the business settled that a stranded round
   *               is the desk's problem to move, not theirs.
   *
   * Written as two rules rather than `canAssign && scope !== "mine"` so that
   * the narrower one cannot be widened by accident later: whoever adds a
   * fourth scope has to say which of the two it behaves like.
   *
   * Reassignment needed nothing on the server. `assignTask` and
   * `assignTasksBulk` have always accepted a task that somebody already
   * holds - only DELIVERED is refused - because a round has always had to be
   * movable when somebody goes off sick. The capability was live and the
   * screen simply never offered it, which is why this is one boolean and not
   * an endpoint.
   */
  const canBulkAssign =
    (canAssign && scope === "unassigned") || (isOverseer && scope === "open");

  /**
   * The picked rows themselves, not just their ids.
   *
   * Delivery Summary is a MIXED list - PENDING, ASSIGNED and FAILED together -
   * so a selection can hold rows with an agent and rows without one, and the
   * button cannot be labelled from the scope. It is labelled from what is
   * actually ticked.
   */
  const pickedTasks = useMemo(
    () => (tasks ?? []).filter((task) => picked.has(task.id)),
    [tasks, picked],
  );
  const isReassignment = pickedTasks.some((task) => task.assignedTo);
  /**
   * Every ticked row is already this agent's, so the action would change
   * nothing - and still report "3 deliveries handed to Ravi", which reads
   * exactly like a reassignment that worked. Caught here rather than on the
   * server because a mixed selection makes it a UI question: some of these
   * moving and some not is a perfectly good action.
   */
  const noOpReassign =
    assignTo !== "" &&
    pickedTasks.length > 0 &&
    pickedTasks.every((task) => task.assignedTo?.id === assignTo);

  /**
   * Where the back arrow goes: the module this screen was entered FROM.
   *
   * Hardcoded "/delivery" until the assignment queue moved into Service, and
   * then it sent anybody who opened it from Service back into Delivery -
   * reported straight away, because it is the sort of thing you notice on the
   * first click.
   *
   * It was worse than a wrong destination for the person the move was FOR: a
   * Service Manager does not hold the DELIVERY module, so the arrow took them
   * to a screen they are bounced off, and they landed on Home. One screen
   * serving two modules has to ask which one it is in, the same way it
   * already asks the path for its scope.
   */
  const backTo = pathname.startsWith("/service") ? "/service" : "/delivery";

  const columns = useColumns<DeliveryTask>(
    () => [
      // The selection column exists only where somebody may act on it. A
      // checkbox that cannot lead anywhere is a control that lies.
      ...(canBulkAssign
        ? [
            {
              key: "select",
              header: "",
              width: "44px",
              render: (task: DeliveryTask) => (
                <Checkbox
                  aria-label={`Select ${task.subjectReference}`}
                  checked={picked.has(task.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const next = new Set(picked);
                    if (event.currentTarget.checked) next.add(task.id);
                    else next.delete(task.id);
                    setPicked(next);
                  }}
                />
              ),
              // No `value`: a tick box is not data.
            },
          ]
        : []),
      {
        key: "Reference",
        header: "Reference",
        width: "170px",
        render: (task) => (
          <Text fz={13} fw={600} className="num" truncate>
            {task.subjectReference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (task) => (
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fz={14} truncate>
              {customerOf(task)?.name ?? "Unknown customer"}
            </Text>
            {customerOf(task)?.address && (
              <Text fz={12} c="gray.6" truncate>
                {customerOf(task)!.address}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: "Carrying",
        header: "What is being carried",
        width: "220px",
        render: (task) => (
          <Text fz={13} c="gray.7" truncate>
            {carryingLabel(task)}
          </Text>
        ),
        value: carryingLabel,
      },
      {
        key: "To collect",
        header: "To collect",
        width: "140px",
        align: "right",
        // The LIVE figure, derived by the server from the bill's payments -
        // never codAmountDue. Marketing collects in the field after a bill is
        // raised, so the snapshot goes stale and asking for money already
        // paid is the mistake this exists to prevent.
        render: (task) =>
          task.collectsMoney ? (
            <Text fz={14} fw={600} className="num">
              {formatINR(task.outstandingAmount)}
            </Text>
          ) : (
            <Text fz={13} c="gray.6">
              nothing
            </Text>
          ),
      },
      {
        key: "Status",
        header: "Status",
        width: "170px",
        render: (task) => (
          <StateBadge
            state={deliveryState(task.status) ?? "dlvPending"}
            label={STATUS_LABELS[task.status] ?? task.status}
          />
        ),
      },
      {
        key: "Agent",
        header: "Assigned to",
        width: "160px",
        render: (task) =>
          task.assignedTo ? (
            <Text fz={13} c="gray.7" truncate>
              {task.assignedTo.name}
            </Text>
          ) : (
            <Text fz={13} c="gray.6">
              nobody yet
            </Text>
          ),
      },
    ],
    // MUST list `picked`: the select column reads it, and with empty deps
    // the column kept the first render's empty set forever - the box
    // ticked and immediately un-ticked, with nothing to say why. A browser
    // check caught it; a typecheck cannot see a stale closure.
    [canBulkAssign, picked],
  );

  async function assignPicked() {
    if (picked.size === 0 || !assignTo) return;
    setAssigning(true);
    try {
      const result = await api.assignDeliveriesBulk([...picked], assignTo);
      notifications.show({
        color: "teal",
        message: `${result.assigned} ${
          result.assigned === 1 ? "delivery" : "deliveries"
        } handed to ${result.assignedTo.name}.`,
      });
      // The selection is spent. It was never cleared before, which nothing
      // noticed while this control only existed on the unassigned queue -
      // there the rows LEAVE the list once they have an agent, so the stale
      // ids matched nothing. On Delivery Summary the rows stay, so the ticks
      // stayed with them: the button went on offering to move deliveries that
      // had just been moved, and "Select all" reported a full selection of a
      // list nobody had ticked.
      setPicked(new Set());
      await load();
    } catch (caught) {
      notifications.show({
        color: "red",
        autoClose: false,
        title: "Nothing was assigned",
        // The endpoint is all-or-nothing, and the message says so rather than
        // leaving a manager wondering which rows moved.
        message:
          caught instanceof Error
            ? caught.message
            : "None of them were assigned. Reload the queue and try again.",
      });
    } finally {
      setAssigning(false);
    }
  }

  return (
    <Page
      title={heading}
      width="wide"
      subtitle={
        tasks
          ? scope === "mine" && user
            ? `${user.name} · ${tasks.length} assigned to you`
            : `${tasks.length} deliver${tasks.length === 1 ? "y" : "ies"}`
          : undefined
      }
      back={backTo}
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!tasks && !error && <LoadingSkeleton variant="card" count={3} />}

      {/* The Today strip. Four facts about the round in hand, each one
          derivable from the list itself - see the note at the top on why
          "1 of 4 done" is not among them. */}
      {showRound && (
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={8}>
          <TodayTile label="stops" value={String(today.stops)} />
          <TodayTile label="units out" value={String(today.units)} />
          <TodayTile
            label="to collect"
            value={formatINR(today.toCollect.toFixed(2))}
          />
          <TodayTile
            label={today.failed === 1 ? "failed attempt" : "failed attempts"}
            value={String(today.failed)}
            alarming={today.failed > 0}
          />
        </SimpleGrid>
      )}

      {nextStop && <NextStop task={nextStop} onOpen={() => navigate(`/delivery/tasks/${nextStop.id}`)} />}

      {canBulkAssign && (tasks?.length ?? 0) > 0 && (
        <Card withBorder padding={12}>
          <Group gap="sm" wrap="wrap" align="flex-end">
            <Checkbox
              label="Select all"
              checked={picked.size > 0 && picked.size === tasks!.length}
              indeterminate={picked.size > 0 && picked.size < tasks!.length}
              onChange={(event) =>
                setPicked(
                  event.currentTarget.checked
                    ? new Set(tasks!.map((task) => task.id))
                    : new Set(),
                )
              }
            />
            <Select
              id="assign-to"
              label="Hand them to"
              placeholder="Choose an agent"
              value={assignTo || null}
              onChange={(value) => setAssignTo(value ?? "")}
              data={agents.map((agent) => ({ value: agent.id, label: agent.name }))}
              searchable
              w={240}
            />
            <Button
              disabled={
                picked.size === 0 || !assignTo || assigning || noOpReassign
              }
              loading={assigning}
              onClick={() => void assignPicked()}
            >
              {isReassignment ? "Reassign" : "Assign"} {picked.size} selected
            </Button>
          </Group>
          <Text fz={12} c="gray.6" mt={8}>
            {noOpReassign
              ? "Every delivery you have ticked is already theirs — choose a different agent."
              : scope === "open"
                ? "Moving a delivery does not undo anything already done on it. All of them or none of them — if one cannot be moved, nothing moves."
                : "All of them or none of them — if one cannot be assigned, nothing moves, so the queue never half-changes under you."}
          </Text>
        </Card>
      )}

      {/* OVERSEERS, not assigners: the business settled that the desk orders
          a round, and the server refuses a Service Manager who tries. */}
      {isOverseer && scope === "open" && (tasks?.length ?? 0) > 0 && (
        <RoundOrdering
          tasks={tasks!}
          agents={agents}
          onSaved={() => void load()}
        />
      )}

      {tasks?.length === 0 && (
        <EmptyState
          title={
            !canAssign
              ? "Nothing is assigned to you"
              : scope === "mine"
                ? "Nothing assigned to you"
                : scope === "unassigned"
                  ? "Nothing waiting"
                  : "Nothing left to deliver"
          }
          description={
            !canAssign
              ? "A delivery appears here when the Billing Executive assigns one to you, or the Store Manager hands you a round."
              : scope === "unassigned"
                ? "Every delivery has an agent."
                : undefined
          }
          action={
            canAssign && scope !== "unassigned" ? (
              <Button onClick={() => navigate("/service/assign-deliveries")}>
                See what needs assigning
              </Button>
            ) : undefined
          }
        />
      )}

      {/* A table for the manager's queues; the agent's round stays as cards
          at every width, because a round is walked one stop at a time and its
          Next stop card is the point of the screen. */}
      {!showRound && (tasks?.length ?? 0) > 0 && (
        <ListView
          rows={tasks!}
          columns={columns}
          rowKey={(task) => task.id}
          {...(canOpenTask
            ? { onOpen: (task: DeliveryTask) => navigate(`/delivery/tasks/${task.id}`) }
            : {})}
          exportDataset="deliveries"
          caption="Deliveries"
          footnote="What is owed is recomputed from the bill's payments, never from the amount recorded when the task was raised."
          renderCard={(task) => (
            <TaskCard
              key={task.id}
              task={task}
              scope={scope}
              {...(canBulkAssign
                ? {
                    selected: picked.has(task.id),
                    onSelect: (checked: boolean) => {
                      const next = new Set(picked);
                      if (checked) next.add(task.id);
                      else next.delete(task.id);
                      setPicked(next);
                    },
                  }
                : {})}
            />
          )}
        />
      )}

      {showRound && rest.length > 0 && (
        <Text fz={12} fw={700} c="gray.6" tt="uppercase" style={{ letterSpacing: ".04em" }}>
          Also assigned to you
        </Text>
      )}

      {rest.map((task) => (
        <TaskCard key={task.id} task={task} scope={scope} />
      ))}

      {nextStop && (
        <StickyActionBar
          primary={
            <Button
              size="md"
              fullWidth
              onClick={() => navigate(`/delivery/tasks/${nextStop.id}`)}
            >
              Open next stop
            </Button>
          }
        />
      )}
    </Page>
  );
}

function TodayTile({
  label,
  value,
  alarming = false,
}: {
  label: string;
  value: string;
  alarming?: boolean;
}) {
  return (
    <Card withBorder padding={10}>
      <Text
        fz={20}
        fw={700}
        className="num"
        lh={1.1}
        c={alarming ? "red.8" : undefined}
      >
        {value}
      </Text>
      <Text fz={12} c="gray.6">
        {label}
      </Text>
    </Card>
  );
}

/**
 * The stop to do next, drawn as one card rather than as the first row of a
 * list.
 *
 * "Next" is the Store Manager's order, read back from the server - not an
 * optimisation this screen invented. An app that silently reordered somebody's
 * round would be making a routing decision nobody asked it to make.
 */
function NextStop({ task, onOpen }: { task: DeliveryTask; onOpen: () => void }) {
  const customer = customerOf(task);
  const phone = phoneOf(task);

  return (
    <Card withBorder padding="md" style={{ borderLeft: "3px solid #1864ab" }}>
      <Stack gap={8}>
        <Group justify="space-between" wrap="nowrap">
          <Text fz={12} fw={700} c="gray.6" tt="uppercase" style={{ letterSpacing: ".04em" }}>
            Next stop
          </Text>
          <StateBadge
            state={deliveryState(task.status) ?? "dlvAssigned"}
            label={STATUS_LABELS[task.status] ?? task.status}
          />
        </Group>

        <Text fz={17} fw={600}>
          {customer?.name ?? "Unknown customer"}
        </Text>
        {customer?.address && (
          <Text fz={14} c="gray.7">
            {customer.address}
          </Text>
        )}
        <Text fz={13} c="gray.6" className="num">
          {task.subjectReference} · {carryingLabel(task)} · {unitsOf(task)}{" "}
          {unitsOf(task) === 1 ? "unit" : "units"}
        </Text>

        {task.collectsMoney ? (
          <Stack gap={2}>
            <Text fz={17} fw={700} className="num">
              Collect {formatINR(task.outstandingAmount)} at the door
            </Text>
            {partlyCollected(task) && (
              <Text fz={13} c="gray.6">
                The bill was {formatINR(task.order!.bill!.totalAmount)}; the rest
                has already been collected in the field.
              </Text>
            )}
          </Stack>
        ) : (
          <Text fz={14} c="gray.7">
            Nothing to collect.
          </Text>
        )}

        {task.status === "FAILED" && task.failureReason && (
          <Card
            withBorder
            padding={10}
            style={{ background: "#fff5f5", borderColor: "#ffc9c9" }}
          >
            <Text fz={13} c="gray.8">
              Last attempt: {task.failureReason}
            </Text>
            <Text fz={13} c="gray.7">
              The goods are still to be delivered. Take it again to try once
              more.
            </Text>
          </Card>
        )}

        {/* Three 44px actions. Each is a real link the phone can act on -
            nothing here pretends to navigate inside the app. */}
        <Group gap={8} grow>
          <Button
            component="a"
            href={phone ? `tel:${phone}` : undefined}
            disabled={!phone}
            variant="default"
            h={44}
            leftSection={<IconPhone size={16} stroke={1.8} />}
          >
            Call
          </Button>
          <Button
            component="a"
            href={phone ? `https://wa.me/91${phone.replace(/\D/g, "").slice(-10)}` : undefined}
            target="_blank"
            rel="noreferrer"
            disabled={!phone}
            variant="default"
            h={44}
            leftSection={<IconBrandWhatsapp size={16} stroke={1.8} />}
          >
            WhatsApp
          </Button>
          <Button
            component="a"
            href={
              customer?.address
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`
                : undefined
            }
            target="_blank"
            rel="noreferrer"
            disabled={!customer?.address}
            variant="default"
            h={44}
            leftSection={<IconMapPin size={16} stroke={1.8} />}
          >
            Navigate
          </Button>
        </Group>

        <Button size="md" onClick={onOpen}>
          Open this stop
        </Button>
      </Stack>
    </Card>
  );
}

/**
 * The Store Manager puts one agent's round in the order it should run.
 *
 * WHY THE DESK AND NOT THE AGENT
 *
 * The business settled this: the Store Manager sequences the round. So this
 * lives on the manager's screen and an agent's own view has no controls -
 * their round arrives in the order somebody else decided, which is the point.
 *
 * WHY UP AND DOWN RATHER THAN DRAG
 *
 * The brief asks for drag-to-order. Up/down buttons are what is here, and
 * deliberately: this is used on a phone at a desk with one thumb, HTML5 drag
 * does not work on touch without a library, and a drag handle is invisible to
 * the keyboard. Two buttons do the same job for everybody. The cost is that
 * moving a stop five places takes five taps, which for a round of six stops
 * is not a real cost.
 *
 * THE WHOLE ROUND IS POSTED, NOT ONE POSITION
 *
 * Positions are assigned server-side from the array order. A
 * position-at-a-time API would make this screen renumber everything else, so
 * every move would be several writes that can half-fail and surface as two
 * stops claiming position 3.
 */
function RoundOrdering({
  tasks,
  agents,
  onSaved,
}: {
  tasks: DeliveryTask[];
  agents: DeliveryAgent[];
  onSaved: () => void;
}) {
  const [agentId, setAgentId] = useState<string>("");
  const [order, setOrder] = useState<DeliveryTask[]>([]);
  const [saving, setSaving] = useState(false);

  // Whose rounds there are to order - taken from the work itself, so an agent
  // with nothing assigned is not offered as a round to sequence.
  const withWork = useMemo(() => {
    const ids = new Set(
      tasks.map((task) => task.assignedTo?.id).filter((id): id is string => Boolean(id)),
    );
    return agents.filter((agent) => ids.has(agent.id));
  }, [tasks, agents]);

  useEffect(() => {
    setOrder(tasks.filter((task) => task.assignedTo?.id === agentId));
  }, [agentId, tasks]);

  function move(index: number, delta: number) {
    const next = [...order];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setOrder(next);
  }

  async function save() {
    if (!agentId || order.length === 0) return;
    setSaving(true);
    try {
      await api.reorderRound(agentId, order.map((task) => task.id));
      notifications.show({
        color: "teal",
        message: `Round set for ${withWork.find((a) => a.id === agentId)?.name ?? "the agent"}.`,
      });
      onSaved();
    } catch (caught) {
      notifications.show({
        color: "red",
        autoClose: false,
        title: "The round was not changed",
        message: caught instanceof Error ? caught.message : "Try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card withBorder padding={12}>
      <Stack gap="sm">
        <Text fz={12} fw={700} c="gray.6" tt="uppercase" style={{ letterSpacing: ".04em" }}>
          Order a round
        </Text>
        <Select
          id="round-agent"
          label="Whose round"
          placeholder="Choose an agent"
          value={agentId || null}
          onChange={(value) => setAgentId(value ?? "")}
          data={withWork.map((agent) => ({ value: agent.id, label: agent.name }))}
          searchable
          w={260}
        />

        {agentId && order.length === 0 && (
          <Text fz="sm" c="gray.6">
            Nothing is assigned to them.
          </Text>
        )}

        {order.map((task, index) => (
          <Group key={task.id} gap="sm" wrap="nowrap">
            <Text fz={14} fw={700} className="num" w={24} ta="right">
              {index + 1}
            </Text>
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text fz={14} truncate>
                {customerOf(task)?.name ?? "Unknown customer"}
              </Text>
              <Text fz={12} c="gray.6" className="num" truncate>
                {task.subjectReference}
                {customerOf(task)?.address ? ` · ${customerOf(task)!.address}` : ""}
              </Text>
            </Stack>
            <Button
              variant="default"
              size="compact-sm"
              aria-label={`Move ${task.subjectReference} earlier`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              <IconArrowUp size={14} stroke={2} />
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              aria-label={`Move ${task.subjectReference} later`}
              disabled={index === order.length - 1}
              onClick={() => move(index, 1)}
            >
              <IconArrowDown size={14} stroke={2} />
            </Button>
          </Group>
        ))}

        {order.length > 0 && (
          <Group gap="sm">
            <Button loading={saving} onClick={() => void save()}>
              Save this order
            </Button>
            <Text fz={12} c="gray.6">
              The agent sees the round in this order. A stop with no position
              set runs last.
            </Text>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

/**
 * One delivery as a card: the phone's row, and the mobile half of the
 * manager's queue.
 *
 * Extracted when the desktop queue became a table - the same row had to be
 * drawn in two places, and a second copy is a second set of words to keep in
 * step with the first.
 */
function TaskCard({
  task,
  scope,
  selected,
  onSelect,
}: {
  task: DeliveryTask;
  scope: DeliveryScope;
  /** Present only where a manager is handing work out in bulk. */
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
}) {
  const customer = customerOf(task);
  const row = (
    <RecordCard
      to={`/delivery/tasks/${task.id}`}
      primary={task.subjectReference}
      secondary={customer?.name ?? "Unknown customer"}
      // A failed attempt is the row to find first: the goods still have to
      // get there, and a second attempt starts from this list.
      accent={task.status === "FAILED" ? "red-7" : undefined}
      badges={
        <>
          <StateBadge
            state={deliveryState(task.status) ?? "dlvPending"}
            label={STATUS_LABELS[task.status] ?? task.status}
          />
          {task.assignedTo && scope !== "mine" && (
            <StateBadge state="dlvAssigned" label={task.assignedTo.name} />
          )}
        </>
      }
      action={
        /* The live figure, not codAmountDue. A collection may already have
           been taken against this bill in the field, and asking for money
           already paid is the mistake this exists to prevent. */
        task.collectsMoney ? (
          <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
            <Text fz="sm" c="gray.6">
              Collect
            </Text>
            <Text fz="lg" fw={700} c="gray.9" className="num">
              {formatINR(task.outstandingAmount)}
            </Text>
          </Stack>
        ) : undefined
      }
      meta={
        <>
          {customer?.address ? `${customer.address} · ` : ""}
          {carryingLabel(task)}
          {!task.collectsMoney ? " · nothing to collect" : ""}
          {task.collectsMoney && partlyCollected(task)
            ? ` · bill was ${formatINR(task.order!.bill!.totalAmount)}, part already collected`
            : ""}
          {task.failureReason && task.status === "FAILED"
            ? ` · last attempt: ${task.failureReason}`
            : ""}
        </>
      }
    />
  );

  if (!onSelect) return row;
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <Checkbox
        mt={14}
        aria-label={`Select ${task.subjectReference}`}
        checked={selected ?? false}
        onChange={(event) => onSelect(event.currentTarget.checked)}
      />
      <Box style={{ flex: 1, minWidth: 0 }}>{row}</Box>
    </Group>
  );
}
